import { useSpaUrl } from "@serophin/browser";
import { useEffect, useState } from "preact/hooks";
import { route } from "preact-router";
import type { SettingsTab } from "../../components/settings/SettingsMenu";
import { hasSession } from "../../platform/session";
import { currentPath, unauthorizedUrl } from "./useRequireSession";

const TABS: readonly string[] = ["narrator", "appearance", "reading", "email", "account"];
const SIGNED_IN_TABS: readonly string[] = ["email", "account"];

/** The tab named by `?tab=`; a tab that needs a session sends a signed-out visitor to sign in. */
const tabFromUrl = (): SettingsTab | null => {
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (!tab || !TABS.includes(tab)) {
    return null;
  }
  if (SIGNED_IN_TABS.includes(tab) && !hasSession()) {
    route(unauthorizedUrl(currentPath()), true);
    return null;
  }
  return tab as SettingsTab;
};

const writeUrl = (tab: SettingsTab | null): void => {
  const url = new URL(window.location.href);
  if (tab) {
    url.searchParams.set("tab", tab);
  } else {
    url.searchParams.delete("tab");
  }
  window.history.pushState({}, "", url.toString());
};

export type SettingsTabState = {
  tab: SettingsTab | null;
  /** Opens a panel (or the menu, with null) and records it in the history, so Back closes it. */
  open: (tab: SettingsTab | null) => void;
};

export const useSettingsTab = (): SettingsTabState => {
  const url = useSpaUrl();
  const [tab, setTab] = useState<SettingsTab | null>(tabFromUrl);

  useEffect(() => {
    setTab(tabFromUrl());
  }, [url]);

  useEffect(() => {
    const onPopState = () => setTab(tabFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const open = (next: SettingsTab | null) => {
    setTab(next);
    writeUrl(next);
  };

  return { tab, open };
};
